/**
 * Gemini Omni Flash PREVIEW (`gemini-omni-flash-preview`) — date-gated retirement.
 *
 * DEPENDENCY-FREE on purpose: the browser imports this file (VeoVideoService,
 * NodeAdapters, nodeCreditQuote, UniversalNode) as well as the server (the
 * /api/gemini-omni route and the headless ProviderService). Do not add imports.
 *
 * WHAT IS RETIRED — and what is NOT
 *   Only the PREVIEW model id. The Gemini Omni NODE stays live: since migration
 *   125 (2026-09-01) it defaults to `gemini-omni-1.1-flash`, Google's GA
 *   successor, which has no announced shutdown date. The preview was removed
 *   from the node dropdown then, but flows saved before that still pin it.
 *
 * SOURCE (verified 2026-09-24)
 *   https://ai.google.dev/gemini-api/docs/deprecations
 *     gemini-omni-flash-preview — released 2026-06-30, shutdown 2026-09-30,
 *     recommended replacement gemini-omni-1.1-flash.
 *   https://ai.google.dev/gemini-api/docs/changelog (2026-08-27):
 *     "The existing gemini-omni-flash-preview endpoint will be deprecated on
 *      September 30, 2026."
 *   Google gives no time of day and no time zone. We refuse from 00:00 UTC on
 *   the shutdown date — the earliest reading — so no request is reserved and
 *   then fails on the day itself.
 *
 * BEFORE the date the preview keeps running (Google still serves it); saved
 * flows see a "retiring" notice. FROM the date the route, headless and the
 * editor refuse it with 410 "Model retired" BEFORE any credit reservation.
 *
 * NOT ALIASED onto gemini-omni-1.1-flash: CLAUDE.md "Aliasing a retired model".
 * The two ids happen to price the same today (60 cr/s @720p), but an alias
 * silently changes which model a saved flow runs; the owner chose an explicit
 * refusal naming the replacement instead.
 */

export const GEMINI_OMNI_PREVIEW_MODEL = 'gemini-omni-flash-preview';
export const GEMINI_OMNI_REPLACEMENT_MODEL = 'gemini-omni-1.1-flash';

/** THE shutdown instant. The only place the date lives. */
export const GEMINI_OMNI_PREVIEW_SHUTDOWN_UTC = '2026-09-30T00:00:00.000Z';
const SHUTDOWN_MS = Date.parse(GEMINI_OMNI_PREVIEW_SHUTDOWN_UTC);

/**
 * Injectable clock. Production code calls `retirementClock.now()`; tests
 * replace it (`jest.spyOn(retirementClock, 'now')` / `vi.spyOn(...)`) instead of
 * faking global timers.
 */
export const retirementClock = {
  now: (): Date => new Date(),
};

/** True when `model` is the preview id (retired or not yet). */
export function isGeminiOmniPreviewModel(model: unknown): boolean {
  return model === GEMINI_OMNI_PREVIEW_MODEL;
}

/** True once the preview's shutdown instant has passed and `model` is the preview. */
export function isGeminiOmniModelRetired(model: unknown, now: Date = retirementClock.now()): boolean {
  return isGeminiOmniPreviewModel(model) && now.getTime() >= SHUTDOWN_MS;
}

/** The 410 refusal text for a retired id, or undefined if `model` may still run. */
export function geminiOmniRetiredMessage(model: unknown, now: Date = retirementClock.now()): string | undefined {
  if (!isGeminiOmniModelRetired(model, now)) return undefined;
  return 'Gemini Omni Flash (preview) was shut down by Google on 2026-09-30 and can no longer generate. ' +
    'Open this node\'s settings and choose "Gemini Omni 1.1 Flash" (same price, 720p) to keep using it.';
}

/**
 * The on-node notice for a saved node pinning the preview: a warning before the
 * date, the retired message after. Undefined for any other model.
 */
export function geminiOmniPreviewNotice(
  model: unknown,
  now: Date = retirementClock.now(),
): { retired: boolean; message: string } | undefined {
  if (!isGeminiOmniPreviewModel(model)) return undefined;
  if (isGeminiOmniModelRetired(model, now)) {
    return { retired: true, message: 'Retired: Gemini Omni Flash (preview) — switch to Gemini Omni 1.1 Flash' };
  }
  return { retired: false, message: 'Retiring 2026-09-30: switch to Gemini Omni 1.1 Flash' };
}
